import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface BarChartProps {
  title: string;
  data: [string, number][];
  color?: string;
  horizontal?: boolean;
  enableSeeMore?: boolean;      // show small "See more" button (for compact cards)
  onToggleSeeMore?: () => void; // parent callback to show separate full chart

  // parent-controlled search/sidebar (used only when showSidebar is true)
  onSearchChange?: (value: string) => void;
  selectedItem?: [string, number] | null;

  // NEW: whether to render the 8:2 split sidebar. Only enable for the FULL chart.
  showSidebar?: boolean;
}

const colorScale = [
  "#8FA7C7",
  "#E2E8F0",
  "#9DBAD5",
  "#B7C8DB",
  "#98AFC7",
  "#C0D3E0",
];

const backgroundBarPlugin = {
  id: "backgroundBar",
  beforeDatasetsDraw(chart: any) {
    const { ctx, scales } = chart;
    const yAxis = scales.y;
    const xAxis = scales.x;
    const dataset = chart.getDatasetMeta(0);

    ctx.save();
    ctx.fillStyle = "rgba(180, 180, 180, 0.15)";
    dataset.data.forEach((bar: any) => {
      if (chart.config.options.indexAxis === "y") {
        ctx.fillRect(xAxis.left, bar.y - bar.height / 2, xAxis.width, bar.height);
      } else {
        ctx.fillRect(bar.x - bar.width / 2, yAxis.top, bar.width, yAxis.height);
      }
    });
    ctx.restore();
  },
};

const ReusableBarChart: React.FC<BarChartProps> = ({
  title,
  data,
  color,
  horizontal = false,
  enableSeeMore = false,
  onToggleSeeMore,
  onSearchChange,
  selectedItem,
  showSidebar = false,
}) => {
  if (data.length === 0) {
    return (
      <div className="p-4 bg-white shadow-md rounded-md flex items-center justify-center h-64">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <svg
            className="w-5 h-5 animate-spin text-blue-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          Loading chart data...
        </div>
      </div>
    );
  }

  const sortedData = [...data].sort((a, b) => b[1] - a[1]);

  const makeChartData = (dataset: [string, number][]) => {
    const labels = dataset.map(([name]) => name);
    const values = dataset.map(([_, value]) => value);

    const min = Math.min(...values);
    const max = Math.max(...values);
    const getColor = (value: number) => {
      if (color) return color;
      const steps = colorScale.length;
      const stepSize = max === min ? 1 : (max - min) / steps;
      const index = Math.floor((value - min) / stepSize);
      return colorScale[Math.min(index, colorScale.length - 1)];
    };

    return {
      labels,
      datasets: [
        {
          label: "",
          data: values,
          backgroundColor: values.map((v) => getColor(v)),
          borderColor: "#fff",
          borderWidth: 1,
          borderRadius: 6,
          hoverBackgroundColor: values.map((v) => `${getColor(v)}CC`),
        },
      ],
    };
  };

  const chartOptions = (labels: string[]) => ({
    indexAxis: horizontal ? ("y" as const) : ("x" as const),
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: title,
        font: { size: 14, weight: "bold" as const },
        padding: { top: 10, bottom: 20 },
      },
      tooltip: {
        backgroundColor: "#fff",
        titleColor: "#333",
        bodyColor: "#333",
        borderColor: "#ddd",
        borderWidth: 1,
        padding: 10,
        callbacks: {
          label: (context: any) => {
            const label = labels[context.dataIndex];
            const value = context.raw;
            return `${label}: ${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { display: false },
        ticks: {
          maxRotation: horizontal ? 0 : 30,
          minRotation: horizontal ? 0 : 30,
          font: { size: 10 },
          callback: (value: string | number) => {
            const label = typeof value === "number" ? labels[value] : value;
            return label.length > 7 ? `${label.substring(0, 7)}...` : label;
          },
        },
      },
      y: {
        grid: { color: "#eee" },
        ticks: {
          font: { size: 10 },
          callback: (value: string | number) => {
            if (typeof value === "number") {
              if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
              if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
              return value.toString();
            }
            return value;
          },
        },
      },
    },
  });

  // The core Bar element we reuse in both compact & full modes
  const barElement = (
    <Bar
      data={makeChartData(sortedData)}
      options={chartOptions(sortedData.map(([n]) => n))}
      plugins={[backgroundBarPlugin]}
    />
  );

  return (
  <div className="p-4 bg-white shadow-md rounded-md">
    {/* Compact chart only */}
    <div style={{ height: "300px", width: "100%" }}>
      {barElement}
    </div>

    {/* "See more" button (parent decides what happens when clicked) */}
    {enableSeeMore && onToggleSeeMore && (
      <div className="flex justify-end mt-2">
        <button
          onClick={onToggleSeeMore}
          style={{ color: "#2563EB", fontSize: "0.75rem" }}
          className="hover:underline"
        >
          See more
        </button>
      </div>
    )}
  </div>
);

};

export default ReusableBarChart;